import {
    AfterViewInit,
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    QueryList,
    ViewChild,
    ViewChildren
} from "@angular/core";
import {UtilityService} from "@irb-common/services/utility.service";
import {ValidationService} from "@irb-common/services/validation.service";
import {EditorsPage} from "@irb/irb-editors/irb-editors.page";
import {IrbEditorsService} from "@irb/irb-editors/irb-editors.service";
import {AppService} from "@irb/app.service";
import {IEvent, IEventSubscriber, IrbEditorsEvents} from "@irb-common/events";
import {PageService} from "@irb-common/services/page.service";
import {Observable, of, Subscription} from "rxjs";
import {ServiceLocator} from "@irb-common/services/locator.service";
import {CalltoactionService} from "@irb/irb-common/components/calltoaction/calltoaction.service";
import {HoverPanelService} from "@irb/irb-common/components/hover-panel/hover-panel.service";
import { AnalyticsService } from "@irb/irb-common/analytics/analytics.service";

@Directive()
export class IrbEditor implements EditorsPage<any>, IEventSubscriber, OnDestroy, AfterViewInit {
    @ViewChild("body") body: ElementRef;
    @ViewChildren("body") elements: QueryList<ElementRef>;
    @Input() slot: string;
    ignoreUnsavedChanges: boolean = false;
    _data: any;
    originalData: any;
    _isModified: boolean;
    _isLoading: boolean = true;
    eventSubscription: Subscription;
    changeSubscription: Subscription;
    showUnsavedChangesWarning: boolean;
    identifier: string;
    protected bodyHeight: string;
    protected pendingFunction: Function;
    protected hasPendingFunction: boolean = false;
    protected ies: IrbEditorsService;
    protected util: UtilityService;
    protected as: AppService;
    protected ps: PageService;
    protected vs: ValidationService;
    protected ctas: CalltoactionService;
    protected hps: HoverPanelService;
    protected ats: AnalyticsService;
    protected getEvent: IEvent;
    _isValid: boolean = true;

    constructor() {
        this.ies = ServiceLocator.injector.get(IrbEditorsService);
        this.util = ServiceLocator.injector.get(UtilityService);
        this.as = ServiceLocator.injector.get(AppService);
        this.ps = ServiceLocator.injector.get(PageService);
        this.vs = ServiceLocator.injector.get(ValidationService);
        this.ctas = ServiceLocator.injector.get(CalltoactionService);
        this.hps = ServiceLocator.injector.get(HoverPanelService);
        this.ats = ServiceLocator.injector.get(AnalyticsService);
        this.eventSubscription = this.as.getEvents().subscribe((s) => {
            this.processEvent(s);
        });
    }

    get isValid() {
        return this._isValid;
    }

    ngOnDestroy() {
        if (this.eventSubscription) {
            this.eventSubscription.unsubscribe();
        }
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
        }
        if (this.ies.getCustomCategoriesObservable$)
            this.ies.getCustomCategoriesObservable$ = null;
        if (this.ies.getSecuritiesObservable$)
            this.ies.getSecuritiesObservable$ = null;
    }

    validateInput(): Observable<string[]> {
        const errors: string[] = [];
        return of(errors);
    }

    set isValid(newIsValid: boolean) {
        this._isValid = newIsValid;
    }

    ngAfterViewInit() {
        this.changeSubscription = this.elements.changes.subscribe(() => {
            if (this.body) {
                this.body.nativeElement.addEventListener('scroll', () => {
                    this.ctas.redraw();
                    this.hps.redraw();
                });
            }
        });
    }

    load(data: any): void {
        this.data = data;
        this.originalData = this.util.clone(this.data);
        this.isModified = false;
        this.showUnsavedChangesWarning = false;
        this.hasPendingFunction = false;
        this.isLoading = false;
        this.as.fireEvent(IrbEditorsEvents.IS_NOT_MODIFIED, {"slot": this.slot});
        this.as.fireEvent(IrbEditorsEvents.DATA_LOADED);
    }

    save(): void {
    }

    get(event: IEvent): void {
        this.isLoading = true;
        this.getEvent = event;
    }

    getOriginalData() {
        return this.originalData;
    }

    close(event?: IEvent) {
        let d = (typeof event !== "undefined") ? event.data : {"slot": this.slot};
        this.isModified = false;
        this.isValid = true;
        this.isLoading = true;
        this.data = null;
        this.as.fireEvent(IrbEditorsEvents.IS_NOT_MODIFIED, d);
    }

    processEvent(event: IEvent): void {
        const targetSlot: string = (event.data) ? event.data.slot || "A" : "A";

        if (targetSlot !== this.slot) {
            return;
        }

        switch (event.type) {
            case IrbEditorsEvents.CLOSE:
                this.close(event);
                break;
            case IrbEditorsEvents.CANCEL:
                this.checkForModifications(() => {
                    this.closeWithoutSave(event);

                    if (event.data.pendingFunction) {
                        event.data.pendingFunction();
                    }
                });
                break;
            case IrbEditorsEvents.SAVE:
                if (event.data.pendingFunction) {
                    this.pendingFunction = event.data.pendingFunction;
                    this.hasPendingFunction = true;
                }
                
                this.save();
                break;
            case IrbEditorsEvents.UNSAVED_CHANGES:
                if (this.isModified) {
                    if (event.data.pendingFunction) {
                        this.pendingFunction = event.data.pendingFunction;
                        this.hasPendingFunction = true;
                    }

                    this.showUnsavedChangesWarning = (this.ignoreUnsavedChanges) ? false : true;
                }
                break;
            case IrbEditorsEvents.REVERT_CHANGES:
                this.revert();
                break;
            case IrbEditorsEvents.CALL_PENDING_FUNCTION:
                if (this.hasPendingFunction) {
                    this.pendingFunction();
                    this.hasPendingFunction = false;
                    this.isModified = false;
                }
                break;
            case IrbEditorsEvents.LOAD:
                if (!this.hasPendingFunction) {
                    if (this.isModified) {
                        this.showUnsavedChangesWarning = true;
                        this.hasPendingFunction = true;
                        if (this.data && event.data.data && this.data[this.identifier] === event.data.data[this.identifier]) {
                            this.pendingFunction = () => {
                                this.closeWithoutSave();
                            };
                        } else {
                            this.pendingFunction = () => {
                                this.get(event);
                            };
                        }
                    } else {
                        if (this.data && event.data.data && this.data[this.identifier] === event.data.data[this.identifier]) {
                            this.closeWithoutSave();
                        } else {
                            this.get(event);
                        }
                    }
                }
                break;
            case IrbEditorsEvents.CHANGE_OF_ISVALID:
                this.isValid = event.data;
                break;
        }
    }

    onInputChange(ignoreList?: Array<string>) {
        window.setTimeout(() => {
            this.isModified = !this.util.compare(this.originalData, this.data, ignoreList);
            this.as.fireEvent((this.isModified) ? IrbEditorsEvents.IS_MODIFIED : IrbEditorsEvents.IS_NOT_MODIFIED, {"slot": this.slot});
            this.as.fireEvent(IrbEditorsEvents.DATA_CHANGED);
            this.showUnsavedChangesWarning = false;
            this.hasPendingFunction = false;
        });
    }

    closeWithoutSave(event?: IEvent) {
        let d = (typeof event !== "undefined") ? event.data : {"slot": this.slot};
        this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, d);
    }

    revert() {
        if (this.isModified) {
            this.load(this.originalData);
        }
    }

    closeWithCheck() {
        // this.ignoreUnsavedChanges = false;
        if (this.ignoreUnsavedChanges) {
            this.closeWithoutSave();
        } else {
            this.cancel();
        }
    }

    protected get isModified() {
        return this._isModified;
    }

    protected set isModified(newIsModified: boolean) {
        this._isModified = newIsModified;
    }

    get data() {
        return this._data;
    }

    set data(newData: any) {
        this._data = newData;
    }

    get isLoading() {
        return this._isLoading;
    }

    set isLoading(b: boolean) {
        this._isLoading = b;
    }

    cancel() {
        this.checkForModifications(() => {
            this.closeWithoutSave();
        });
    }

    checkForModifications(f: Function) {
        if (this.isModified && !this.ignoreUnsavedChanges) {
            this.showUnsavedChangesWarning = true;
            this.hasPendingFunction = true;
            this.pendingFunction = f;
        } else {
            f();
        }
    }

    @HostListener("window:resize")
    onResize() {
        if (this.body) {
            const rect = this.body.nativeElement.getBoundingClientRect();
            let h = window.innerHeight - rect.top - 10;

            h = h < 200 ? 200 : h;
            this.bodyHeight = h + "px";
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: any) {
        if (this.isModified && !this.ignoreUnsavedChanges) {
            event.returnValue = false;

            this.checkForModifications(() => {
                this.closeWithoutSave();
            });
        }
    }
}
